import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Tooltip,
    Typography
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BuildIcon from "@mui/icons-material/Build";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAllPuzzleTypes } from "./PuzzleTypeConstants";
import ApiService from "../../services/ApiService";
import DictionaryApiService from "../system/integrate/DictionaryApiService";
import AdminGridBoardService from "../pzl_grid/board/AdminGridBoardService";
import AdminCrossBoardService from "../pzl_cross/board/AdminCrossBoardService";
import Logger from "../../services/Logger";
import {
    getFilteredPuzzleTypes,
    getHiddenMessageFromPuzzleInfo,
    getGridPuzzleCompletionStatus,
    getCrosswordCompletionStatus,
    getWordsToResolve,
    isConfirmDisabled,
    isMiniWordSearchBoard,
} from "./util/VerifyLogic";

export default function VerifyDialog({
    open,
    onClose,
    puzzleId,
    requestPuzzle,
    defaultPuzzleType = 'WORD_SEARCH',
    allowedPuzzleTypes = null, // If provided, only show these puzzle types (array of type IDs)
    onPuzzleTypeSelected,
    onShowSnackbar,
    onRemoveWords = null // (updatedFilledCoordinates: Array) => Promise<void> — for grid: remove words using conflict cells
}) {
    const [selectedType, setSelectedType] = useState(null);
    const [puzzleInfo, setPuzzleInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [infoLoading, setInfoLoading] = useState(false);
    const [bannedWordsCheckResult, setBannedWordsCheckResult] = useState(null);
    const [bannedWordsLoading, setBannedWordsLoading] = useState(false);
    const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
    const [duplicateCheckLoading, setDuplicateCheckLoading] = useState(false);
    const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
    const [removingEntryIndex, setRemovingEntryIndex] = useState(null);
    const [containedWordsAcceptedChecked, setContainedWordsAcceptedChecked] = useState(false);
    const [duplicateHiddenMessageAcceptedChecked, setDuplicateHiddenMessageAcceptedChecked] = useState(false);
    const [titleCheckResult, setTitleCheckResult] = useState(null);
    const [titleCheckLoading, setTitleCheckLoading] = useState(false);
    const [titleHookLoading, setTitleHookLoading] = useState(false);

    const filteredPuzzleTypes = useMemo(() => getFilteredPuzzleTypes(allowedPuzzleTypes), [allowedPuzzleTypes]);

    // Function to check banned words (when difficultyLevel is EASY, high_frequency words are not flagged)
    const checkBannedWords = async (words, language, isKids = false, difficultyLevel = null) => {
        if (!words || words.length === 0 || !language) {
            setBannedWordsCheckResult(null);
            return;
        }

        setBannedWordsLoading(true);
        try {
            const result = await DictionaryApiService.checkBannedWords(words, language, isKids, difficultyLevel);
            setBannedWordsCheckResult(result);
        } catch (error) {
            Logger.error("Error checking banned words:", error);
            setBannedWordsCheckResult(null);
        } finally {
            setBannedWordsLoading(false);
        }
    };

    // Function to check for duplicate hidden messages
    const checkHiddenMessageDuplicate = useCallback(async (hiddenMessage, language, excludeTplId) => {
        if (!hiddenMessage || !language) {
            setDuplicateCheckResult(null);
            return;
        }

        setDuplicateCheckLoading(true);
        try {
            const result = await AdminGridBoardService.checkHiddenMessageDuplicate(hiddenMessage, language, excludeTplId);
            setDuplicateCheckResult(result);
        } catch (error) {
            Logger.error("Error checking hidden message duplicate:", error);
            setDuplicateCheckResult(null);
        } finally {
            setDuplicateCheckLoading(false);
        }
    }, []);

    const getHiddenMessage = useMemo(() => getHiddenMessageFromPuzzleInfo(puzzleInfo), [puzzleInfo]);

    // Check for duplicate when HIDDEN_MESSAGE is selected and hidden message exists
    useEffect(() => {
        if (selectedType?.id === 'HIDDEN_MESSAGE' && getHiddenMessage && puzzleInfo?.language) {
            checkHiddenMessageDuplicate(getHiddenMessage, puzzleInfo.language, puzzleId);
        } else {
            setDuplicateCheckResult(null);
        }
    }, [selectedType, getHiddenMessage, puzzleInfo?.language, puzzleId, checkHiddenMessageDuplicate]);

    // Check title uniqueness (grid-tpl for WORD_SEARCH/HIDDEN_MESSAGE, cross-tpl for CROSSWORD)
    const checkTitle = useCallback(async (tplId, puzzleTypeId) => {
        if (!tplId) {
            setTitleCheckResult(null);
            return;
        }
        setTitleCheckLoading(true);
        setTitleCheckResult(null);
        try {
            const isGrid = puzzleTypeId === "WORD_SEARCH" || puzzleTypeId === "HIDDEN_MESSAGE";
            const path = isGrid
                ? `/v1/puzzle/grid-tpl/check-title/${tplId}`
                : `/v1/puzzle/cross-tpl/check-title/${tplId}`;
            const result = await ApiService.getData(path);
            setTitleCheckResult(result);
        } catch (err) {
            Logger.error("Error checking title:", err);
            setTitleCheckResult(null);
        } finally {
            setTitleCheckLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open || !puzzleId || !selectedType) {
            setTitleCheckResult(null);
            return;
        }
        checkTitle(puzzleId, selectedType.id);
    }, [open, puzzleId, selectedType?.id, checkTitle]);

    useEffect(() => {
        if (open && puzzleId && requestPuzzle) {
            setInfoLoading(true);
            requestPuzzle(puzzleId)
                .then((r) => {
                    setPuzzleInfo(r);
                    // Use existing puzzle type if available and allowed, otherwise use default
                    let puzzleType = r.type || defaultPuzzleType;

                    // If the puzzle type is not in allowed types, use the first allowed type or default
                    if (allowedPuzzleTypes && Array.isArray(allowedPuzzleTypes) && allowedPuzzleTypes.length > 0) {
                        if (!allowedPuzzleTypes.includes(puzzleType)) {
                            puzzleType = allowedPuzzleTypes[0] || defaultPuzzleType;
                        }
                    }

                    // Mini is always WORD_SEARCH (cannot become HIDDEN_MESSAGE).
                    if (isMiniWordSearchBoard(r)) {
                        puzzleType = "WORD_SEARCH";
                    }

                    setSelectedType(filteredPuzzleTypes.find(type => type.id === puzzleType) || filteredPuzzleTypes[0]);

                    // Check banned words if wordsInUse exists (skip high_frequency when EASY)
                    if (r.wordsInUse && r.language) {
                        const words = r.wordsInUse.split('\n').filter(word => word.trim());
                        if (words.length > 0) {
                            checkBannedWords(words, r.language, r.isKids || false, r.difficultyLevel);
                        } else {
                            setBannedWordsCheckResult(null);
                        }
                    } else {
                        setBannedWordsCheckResult(null);
                    }
                })
                .catch((error) => {
                    Logger.error("Error fetching puzzle info:", error);
                    onShowSnackbar("Error loading puzzle information", "error");
                })
                .finally(() => {
                    setInfoLoading(false);
                });
        } else {
            // Reset banned words when dialog closes
            setBannedWordsCheckResult(null);
        }
    }, [open, puzzleId, requestPuzzle, defaultPuzzleType, allowedPuzzleTypes, filteredPuzzleTypes, onShowSnackbar]);

    const handleConfirm = async () => {
        if (!selectedType) {
            onShowSnackbar("Unable to determine puzzle type", "error");
            return;
        }
        if (bannedWordsCheckResult?.bannedCount > 0) {
            onShowSnackbar("Cannot proceed: banned words must be removed first", "error");
            return;
        }
        if (titleCheckResult?.exists) {
            onShowSnackbar("Cannot proceed: a puzzle with this title already exists", "error");
            return;
        }

        const isCrossword = selectedType.id === 'CROSSWORD';
        const status = isCrossword ? getCrosswordCompletionStatus(puzzleInfo) : getGridPuzzleCompletionStatus(puzzleInfo);
        const hasContainedWordsOnly = !status.valid && (status.containedWords?.length ?? 0) > 0 && (status.cellConflicts?.length ?? 0) === 0 && (status.wordMismatches?.length ?? 0) === 0 && (status.fullySharedEntryIndices?.length ?? 0) === 0;
        if (!status.valid && !(hasContainedWordsOnly && containedWordsAcceptedChecked)) {
            onShowSnackbar(status.message || "Puzzle validation failed. Please fix the errors above.", "error");
            return;
        }

        setLoading(true);
        try {
            // Call the callback with the selected puzzle type
            await onPuzzleTypeSelected(selectedType.id);
            onClose();
        } catch (error) {
            Logger.error("Error processing puzzle type selection:", error);
            onShowSnackbar("Error processing selection", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSelectedType(null);
        setPuzzleInfo(null);
        setBannedWordsCheckResult(null);
        setTitleCheckResult(null);
        setTitleHookLoading(false);
        setResolveDialogOpen(false);
        setRemovingEntryIndex(null);
        setContainedWordsAcceptedChecked(false);
        setDuplicateHiddenMessageAcceptedChecked(false);
        onClose();
    };

    const handleApplyTitleHook = useCallback(async () => {
        if (!puzzleId || !selectedType || !requestPuzzle) return;
        setTitleHookLoading(true);
        try {
            const isGrid = selectedType.id === "WORD_SEARCH" || selectedType.id === "HIDDEN_MESSAGE";
            const service = isGrid ? AdminGridBoardService : AdminCrossBoardService;
            const result = await service.applyTitleHook(puzzleId);
            if (result?.code === "FAILURE") {
                onShowSnackbar?.(result.message || "Failed to apply title hook", "error");
            } else {
                const fresh = await requestPuzzle(puzzleId);
                setPuzzleInfo(fresh);
                onShowSnackbar?.("Title hook applied successfully", "success");
            }
        } catch (err) {
            Logger.error("Error applying title hook:", err);
            onShowSnackbar?.(err?.message || "Failed to apply title hook", "error");
        } finally {
            setTitleHookLoading(false);
        }
    }, [puzzleId, selectedType, requestPuzzle, onShowSnackbar]);

    const isGridType = selectedType?.id === "WORD_SEARCH" || selectedType?.id === "HIDDEN_MESSAGE";
    const completionStatus = isGridType ? getGridPuzzleCompletionStatus(puzzleInfo) : getCrosswordCompletionStatus(puzzleInfo);
    const hasCellConflicts = (completionStatus?.cellConflicts?.length ?? 0) > 0;
    const hasContainedWords = (completionStatus?.containedWords?.length ?? 0) > 0;
    const hasDuplicates = !!(completionStatus?.hasDuplicates && (completionStatus?.duplicateEntryIndices?.length ?? 0) > 0);
    const hasWordMismatches = (completionStatus?.wordMismatches?.length ?? 0) > 0;
    const hasFullySharedWords = (completionStatus?.fullySharedEntryIndices?.length ?? 0) > 0;
    const hasBannedWords = (bannedWordsCheckResult?.bannedCount ?? 0) > 0;
    const showResolveIcon = (hasCellConflicts || hasContainedWords || hasDuplicates || hasWordMismatches || hasFullySharedWords || hasBannedWords) && typeof onRemoveWords === "function";

    const handleRemoveWordFromResolve = useCallback(async (entry, indexInCoords) => {
        if (typeof onRemoveWords !== "function" || !puzzleId || !requestPuzzle) return;
        const { coords } = getWordsToResolve(puzzleInfo, completionStatus, bannedWordsCheckResult);
        const updated = coords.filter((_, i) => i !== indexInCoords);
        setRemovingEntryIndex(indexInCoords);
        try {
            await onRemoveWords(updated);
            const fresh = await requestPuzzle(puzzleId);
            setPuzzleInfo(fresh);
            onShowSnackbar(`Word "${(entry.text || entry.word || "").trim()}" removed`, "success");
        } catch (err) {
            Logger.error("Error removing word:", err);
            onShowSnackbar(err?.message || "Failed to remove word", "error");
        } finally {
            setRemovingEntryIndex(null);
        }
    }, [onRemoveWords, puzzleId, requestPuzzle, puzzleInfo, completionStatus, bannedWordsCheckResult, onShowSnackbar]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                <Typography variant="h5" component="h2" gutterBottom>
                    Verify Puzzle
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Verifies the puzzle for duplications, conflict cells, and banned words.
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3}>
                    {/* Puzzle Information */}
                    <Grid size={{ xs: 12 }}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Puzzle Information
                                </Typography>
                                {infoLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : puzzleInfo ? (
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Title
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {puzzleInfo.language && (
                                                    <Chip
                                                        label={puzzleInfo.language === 'french' ? 'FR' : 'EN'}
                                                        size="small"
                                                        sx={{ fontWeight: 600, backgroundColor: '#fff59d' }}
                                                    />
                                                )}
                                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                    {puzzleInfo.title || 'Untitled'}
                                                </Typography>
                                            </Box>
                                            {titleCheckLoading ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                    <CircularProgress size={16} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Checking title...
                                                    </Typography>
                                                </Box>
                                            ) : titleCheckResult && (
                                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                    {titleCheckResult.exists ? (
                                                        <>
                                                            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'medium' }}>
                                                                ⚠ Title already exists
                                                            </Typography>
                                                            <Tooltip title="Apply AI title hook to suggest a new title">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={handleApplyTitleHook}
                                                                    disabled={titleHookLoading || !puzzleId}
                                                                    aria-label="Apply title hook"
                                                                    sx={{ p: 0.25 }}
                                                                >
                                                                    {titleHookLoading ? (
                                                                        <CircularProgress size={18} />
                                                                    ) : (
                                                                        <AutoAwesomeIcon fontSize="small" color="primary" />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    ) : titleCheckResult.message ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {titleCheckResult.message}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                                                            ✓ Title is unique
                                                        </Typography>
                                                    )}
                                                </Box>
                                            )}
                                        </Grid>
                                        <Grid size={{ xs: 12 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Valid
                                                </Typography>
                                                {((!completionStatus.valid && (completionStatus.cellConflicts?.length > 0 || completionStatus.containedWords?.length > 0 || completionStatus.hasDuplicates || completionStatus.wordMismatches?.length > 0 || completionStatus.fullySharedEntryIndices?.length > 0)) || hasBannedWords) && showResolveIcon && (
                                                    <Tooltip title={hasBannedWords ? "Resolve: remove banned words from puzzle" : completionStatus.cellConflicts?.length > 0 ? "Resolve: remove words that use conflicting cells" : completionStatus.containedWords?.length > 0 ? "Resolve: remove contained words" : completionStatus.wordMismatches?.length > 0 ? "Resolve: remove words with position/value mismatch" : completionStatus.fullySharedEntryIndices?.length > 0 ? "Resolve: remove words with no exclusive cells (all positions shared)" : "Resolve: remove duplicate words (one at a time, keeps one)"}>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setResolveDialogOpen(true)}
                                                            aria-label={hasBannedWords ? "Resolve banned words" : completionStatus.cellConflicts?.length > 0 ? "Resolve cell conflicts" : completionStatus.containedWords?.length > 0 ? "Resolve contained words" : completionStatus.wordMismatches?.length > 0 ? "Resolve word/position mismatches" : completionStatus.fullySharedEntryIndices?.length > 0 ? "Resolve fully shared words" : "Resolve duplicate words"}
                                                            sx={{ flexShrink: 0, p: 0.25 }}
                                                        >
                                                            <BuildIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {(() => {
                                                const status = completionStatus;
                                                return (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                            <Typography
                                                                variant="body1"
                                                                sx={{
                                                                    fontWeight: 'medium',
                                                                    color: status.valid ? 'success.main' : 'error.main'
                                                                }}
                                                            >
                                                                {status.message}
                                                            </Typography>
                                                        </Box>
                                                        {!status.valid && status.cellConflicts?.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 0.5 }}>
                                                                {status.cellConflicts.slice(0, 10).map((c, i) => (
                                                                    <Typography key={i} variant="body2" color="error">
                                                                        Row {c.row}, Col {c.col}: {c.values.join(" vs ")}
                                                                    </Typography>
                                                                ))}
                                                                {status.cellConflicts.length > 10 && (
                                                                    <Typography variant="body2" color="error">
                                                                        ... and {status.cellConflicts.length - 10} more
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                        {!status.valid && status.wordMismatches?.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 0.5 }}>
                                                                {status.wordMismatches.slice(0, 10).map((m, i) => (
                                                                    <Typography key={i} variant="body2" color="error">
                                                                        &quot;{m.word}&quot;: expected &quot;{m.expected}&quot;, got values &quot;{m.actual}&quot;
                                                                    </Typography>
                                                                ))}
                                                                {status.wordMismatches.length > 10 && (
                                                                    <Typography variant="body2" color="error">
                                                                        ... and {status.wordMismatches.length - 10} more
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        )}
                                                        {!status.valid && status.fullySharedEntryIndices?.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 0.5 }}>
                                                                <Typography variant="body2" color="error">
                                                                    {status.fullySharedEntryIndices.length === 1
                                                                        ? "One word has no exclusive cells (all positions shared with other words)"
                                                                        : `${status.fullySharedEntryIndices.length} word(s) have no exclusive cells (all positions shared with other words)`}
                                                                    — can be removed via Resolve.
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                        {!status.valid && status.containedWords?.length > 0 && (
                                                            <Box sx={{ ml: 2, mt: 0.5, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                                                <Box>
                                                                    {status.containedWords.map((p, i) => (
                                                                        <Typography key={i} variant="body2" color="error">
                                                                            &quot;{p.short}&quot; is fully covered by &quot;{p.long}&quot;
                                                                        </Typography>
                                                                    ))}
                                                                </Box>
                                                                {(status.cellConflicts?.length ?? 0) === 0 && (status.wordMismatches?.length ?? 0) === 0 && (
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={containedWordsAcceptedChecked}
                                                                                onChange={(e) => setContainedWordsAcceptedChecked(e.target.checked)}
                                                                                color="success"
                                                                                sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                                                                            />
                                                                        }
                                                                        label="Checked"
                                                                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        )}
                                                        {status.valid && (
                                                            <Typography variant="body1" color="success.main" sx={{ fontWeight: 'medium' }}>
                                                                ✓ No cell conflicts, no word/position value mismatches, no fully covered words
                                                            </Typography>
                                                        )}
                                                        {bannedWordsLoading ? (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                                                <CircularProgress size={16} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Checking banned words...
                                                                </Typography>
                                                            </Box>
                                                        ) : bannedWordsCheckResult && (
                                                            <>
                                                                {bannedWordsCheckResult.bannedCount > 0 ? (
                                                                    <>
                                                                        <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>
                                                                            Banned words found ({bannedWordsCheckResult.bannedCount}):
                                                                        </Typography>
                                                                        <Box sx={{ ml: 2 }}>
                                                                            <Typography variant="body2" color="error">
                                                                                {bannedWordsCheckResult.bannedWords.join(', ')}
                                                                            </Typography>
                                                                        </Box>
                                                                    </>
                                                                ) : (
                                                                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 'medium' }}>
                                                                        ✓ All {bannedWordsCheckResult.totalChecked} words checked for banned words and valid
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        )}
                                                        {selectedType?.id === 'HIDDEN_MESSAGE' && getHiddenMessage && (
                                                            <>
                                                                {duplicateCheckLoading ? (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <CircularProgress size={16} />
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Checking for duplicates...
                                                                        </Typography>
                                                                    </Box>
                                                                ) : duplicateCheckResult && !duplicateCheckResult.isDuplicate ? (
                                                                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 'medium' }}>
                                                                        ✓ No duplicate hidden message found: {getHiddenMessage.toUpperCase()}
                                                                    </Typography>
                                                                ) : duplicateCheckResult && duplicateCheckResult.isDuplicate ? (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                                        <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'medium' }}>
                                                                            ⚠ Duplicate found: {duplicateCheckResult.count} puzzle{duplicateCheckResult.count !== 1 ? 's' : ''} with the same hidden message: {getHiddenMessage.toUpperCase()}
                                                                        </Typography>
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={duplicateHiddenMessageAcceptedChecked}
                                                                                    onChange={(e) => setDuplicateHiddenMessageAcceptedChecked(e.target.checked)}
                                                                                    color="warning"
                                                                                    sx={{ "& .MuiSvgIcon-root": { fontSize: 18 } }}
                                                                                />
                                                                            }
                                                                            label="Proceed anyway"
                                                                            sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                                                                        />
                                                                    </Box>
                                                                ) : null}
                                                            </>
                                                        )}
                                                    </Box>
                                                );
                                            })()}
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Typography variant="body2" color="error">
                                        Failed to load puzzle information
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Puzzle Type Display - Only show filtered types */}
                    <Grid size={{ xs: 12 }}>
                        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                            {(() => {
                                // Get all puzzle types
                                const allTypes = getAllPuzzleTypes();

                                // CRITICAL: Filter based on allowedPuzzleTypes prop
                                // If allowedPuzzleTypes is provided, ONLY show those types
                                // This ensures WORD_SEARCH and HIDDEN_MESSAGE are excluded when not in allowedPuzzleTypes
                                let typesToShow = allTypes;

                                // Check if allowedPuzzleTypes prop is provided and valid
                                const hasAllowedTypes = allowedPuzzleTypes && Array.isArray(allowedPuzzleTypes) && allowedPuzzleTypes.length > 0;

                                if (hasAllowedTypes) {
                                    // Filter to ONLY show types in allowedPuzzleTypes array
                                    // This will exclude WORD_SEARCH and HIDDEN_MESSAGE if they're not in the array
                                    typesToShow = allTypes.filter(puzzleType => {
                                        return allowedPuzzleTypes.includes(puzzleType.id);
                                    });
                                }

                                // Mini boards are WORD_SEARCH only (no Hidden Message).
                                if (isMiniWordSearchBoard(puzzleInfo)) {
                                    typesToShow = typesToShow.filter((puzzleType) => puzzleType.id === "WORD_SEARCH");
                                }

                                // If selected type is HIDDEN_MESSAGE, exclude CROSSWORD from display
                                if (selectedType?.id === 'HIDDEN_MESSAGE') {
                                    typesToShow = typesToShow.filter(puzzleType => puzzleType.id !== 'CROSSWORD');
                                }

                                if (typesToShow.length === 0) {
                                    return (
                                        <Grid size={{ xs: 12 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No puzzle types available
                                            </Typography>
                                        </Grid>
                                    );
                                }

                                return typesToShow.map((puzzleType) => {
                                    // On mobile (xs), always use full width (column layout)
                                    // On larger screens (md+), use half width if multiple types (side by side)
                                    const gridSize = typesToShow.length === 1 
                                        ? { xs: 12 } 
                                        : { xs: 12, md: 6 };
                                    const isSelected = selectedType?.id === puzzleType.id;
                                    return (
                                        <Grid size={gridSize} key={puzzleType.id} sx={{ display: 'flex' }}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    width: '100%',
                                                    opacity: isSelected ? 1 : 1, // Always show at full opacity since these are the only allowed types
                                                    backgroundColor: isSelected ? 'background.paper' : 'background.paper',
                                                    borderColor: isSelected ? 'primary.main' : 'grey.300',
                                                    borderWidth: isSelected ? 2 : 1,
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="h4" sx={{ mr: 2 }}>
                                                        {puzzleType.icon}
                                                    </Typography>
                                                    <Box>
                                                        <Typography variant="h6" component="h3" gutterBottom>
                                                            {puzzleType.id === "WORD_SEARCH" && isMiniWordSearchBoard(puzzleInfo)
                                                                ? `Word Search Mini (${Number(puzzleInfo.gridRows ?? puzzleInfo.grid_rows)}×${Number(puzzleInfo.gridCols ?? puzzleInfo.grid_cols)})`
                                                                : puzzleType.name}
                                                            {isSelected && puzzleType.id === 'HIDDEN_MESSAGE' && getHiddenMessage && (
                                                                <Typography component="span" variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', ml: 1 }}>
                                                                    : {getHiddenMessage.toUpperCase()}
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {puzzleType.id === "WORD_SEARCH" && isMiniWordSearchBoard(puzzleInfo)
                                                                ? "Find hidden words on a Mini letter grid (6×6–9×9)."
                                                                : puzzleType.description}
                                                        </Typography>
                                                        {isSelected && (
                                                            <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1, fontWeight: 'medium' }}>
                                                                ✓ Automatically selected
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                });
                            })()}
                        </Grid>
                    </Grid>
                </Grid>
                {loading && (
                    <Grid size={{ xs: 12 }} sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Processing: Updating puzzle to Ready to Review...
                        </Typography>
                        <LinearProgress />
                    </Grid>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={
                        !selectedType ||
                        loading ||
                        (duplicateCheckResult?.isDuplicate && !duplicateHiddenMessageAcceptedChecked) ||
                        isConfirmDisabled(
                            selectedType?.id === "CROSSWORD" ? getCrosswordCompletionStatus(puzzleInfo) : getGridPuzzleCompletionStatus(puzzleInfo),
                            containedWordsAcceptedChecked,
                            bannedWordsCheckResult?.bannedCount ?? 0,
                            titleCheckResult?.exists
                        )
                    }
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading ? 'Processing...' : 'Confirm & Proceed'}
                </Button>
            </DialogActions>

            {/* Resolve dialog: cell conflicts or contained words */}
            <Dialog
                open={resolveDialogOpen}
                onClose={() => setResolveDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                aria-labelledby="resolve-dialog-title"
            >
                <DialogTitle id="resolve-dialog-title">
                    {hasBannedWords ? "Resolve banned words" : hasCellConflicts ? "Resolve cell conflicts" : hasContainedWords ? "Resolve contained words" : hasWordMismatches ? "Resolve word/position mismatch" : hasFullySharedWords ? "Resolve fully shared words" : "Resolve duplicate words"}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {hasBannedWords
                            ? "These words are in the banned words list. Remove each word from the puzzle to resolve."
                            : hasCellConflicts
                                ? "These words use cells that have conflicting values. Remove a word to resolve the conflict."
                                : hasContainedWords
                                    ? "These words are fully covered by longer words. Remove a word to resolve the conflict."
                                    : hasWordMismatches
                                        ? "These words have position values that do not spell the word correctly. Remove each word and re-add it to fix."
                                        : hasFullySharedWords
                                            ? "These words have no exclusive cells—every position is shared with other words. They can be safely removed."
                                            : "Duplicate words found. Remove one copy at a time—one instance will be kept."}
                    </Typography>
                    {(() => {
                        const { wordsWithIndex } = getWordsToResolve(puzzleInfo, completionStatus, bannedWordsCheckResult);
                        if (wordsWithIndex.length === 0) {
                            return (
                                <Typography variant="body2" color="text.secondary">
                                    {hasBannedWords ? "No banned words to remove" : hasCellConflicts ? "No words using conflict cells" : hasContainedWords ? "No contained words to remove" : hasWordMismatches ? "No mismatch words to remove" : hasFullySharedWords ? "No fully shared words to remove" : "No duplicate words to remove"} (conflicts may have been resolved).
                                </Typography>
                            );
                        }
                        return (
                            <List dense disablePadding>
                                {wordsWithIndex.map(({ entry, indexInCoords }) => {
                                    const label = (entry.text || entry.word || "").trim() || "(no text)";
                                    const isRemoving = removingEntryIndex === indexInCoords;
                                    return (
                                        <ListItem key={indexInCoords} divider>
                                            <ListItemText primary={label} />
                                            <ListItemSecondaryAction>
                                                <Tooltip title="Remove this word">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleRemoveWordFromResolve(entry, indexInCoords)}
                                                            disabled={isRemoving}
                                                            aria-label="Remove word"
                                                        >
                                                            {isRemoving ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolveDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}
